import { useState } from 'react';

import AutoFixHighRoundedIcon from '@mui/icons-material/AutoFixHighRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState, PageContainer } from '@/shared/ui';

import { useCardPool } from '../../hooks';
import { formatDateTime, formatPoints } from '../../lib/format';
import { useLoyaltyPermissions } from '../../lib/useLoyaltyPermissions';
import { CardPoolStatusChip } from '../components/chips';
import { GeneratePoolDialog } from '../components/GeneratePoolDialog';
import { ImportPoolDialog } from '../components/ImportPoolDialog';
import { CodeText } from '../components/primitives';

/** Free-card pool inventory — list + import + generate (gated by `loyalty.view`). */
export function LoyaltyCardPoolPage() {
  const { canView, canManage } = useLoyaltyPermissions();
  const pool = useCardPool({ page: 0, size: 100 }, { enabled: canView });
  const [importOpen, setImportOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  if (!canView) {
    return (
      <PageContainer title="Пул карт">
        <ErrorState title="Access restricted" message="У вас нет доступа к пулу карт." />
      </PageContainer>
    );
  }

  const cards = pool.data?.content ?? [];
  const available = pool.data?.availableCount;

  return (
    <PageContainer
      title="Пул карт"
      subtitle="Свободные карты для выдачи клиентам"
      actions={
        canManage ? (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<UploadFileRoundedIcon />}
              onClick={() => setImportOpen(true)}
            >
              Импорт
            </Button>
            <Button
              variant="contained"
              startIcon={<AutoFixHighRoundedIcon />}
              onClick={() => setGenerateOpen(true)}
            >
              Сгенерировать
            </Button>
          </Stack>
        ) : undefined
      }
    >
      {!canManage ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          Просмотр без права изменения. Для импорта/генерации нужно разрешение «loyalty.manage».
        </Alert>
      ) : null}

      {available != null ? (
        <Box sx={{ mb: 2 }}>
          <Chip color="success" variant="outlined" label={`Свободно карт: ${formatPoints(available)}`} />
        </Box>
      ) : null}

      {pool.isLoading ? (
        <LoadingState label="Загрузка пула…" />
      ) : pool.isError ? (
        <ErrorState
          title="Не удалось загрузить пул"
          message={pool.error?.message ?? 'Произошла ошибка.'}
          onRetry={() => void pool.refetch()}
        />
      ) : cards.length === 0 ? (
        <EmptyState
          title="Пул пуст"
          description="Импортируйте карты из файла или сгенерируйте их по диапазону / маске."
          action={
            canManage ? (
              <Button
                variant="contained"
                startIcon={<AutoFixHighRoundedIcon />}
                onClick={() => setGenerateOpen(true)}
              >
                Сгенерировать
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Номер</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Выдана клиенту</TableCell>
                  <TableCell>Когда</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cards.map((card) => (
                  <TableRow key={card.id} hover>
                    <TableCell>
                      <CodeText>{card.cardNumber}</CodeText>
                    </TableCell>
                    <TableCell>
                      <CardPoolStatusChip status={card.status} />
                    </TableCell>
                    <TableCell>
                      {card.issuedToCustomerId ? <CodeText>{card.issuedToCustomerId}</CodeText> : '—'}
                    </TableCell>
                    <TableCell>{formatDateTime(card.issuedAt ?? card.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <ImportPoolDialog open={importOpen} onClose={() => setImportOpen(false)} onDone={(m) => setToast(m)} />
      <GeneratePoolDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        onDone={(m) => setToast(m)}
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast(null)}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast ?? ''}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
